import "@/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReactDOM from "react-dom/client";
import {
	createBrowserRouter,
	createRoutesFromElements,
	Route,
	RouterProvider,
	useRouteError,
} from "react-router-dom";
import { version as appVersion } from "../package.json";
import { initAuth } from "./auth";
import { CookieConsent } from "./components/cookie-consent";
import { ErrorBoundary } from "./components/error-boundary";
import { ErrorFallback } from "./components/error-boundary/ErrorFallback";
import { MisconfiguredPage } from "./components/misconfigured-page";
import {
	APP_ENV,
	CONFIG_ERRORS,
	IS_PRODUCTION,
	IS_SELF_HOSTED,
} from "./constants/constants";
import { getFliptFlag } from "./helpers/get-flipt-flag";
import { fetchIsI18nEnabled } from "./i18n/feature-flag";
import Main from "./modules/main";
import { LocaleGate } from "./router/locale-gate";
import { setTrackedUser, startConsentManagement } from "./services/consent";
import { fetchIsCookieConsentEnabled } from "./services/consent/feature-flag";
import { collectBrowserSignals } from "./services/fraud-sdk";
import { initSentry } from "./services/sentry";
import { initChunkErrorHandler } from "./utils/chunk-error-handler";
import "./assets/fonts/fonts.css";
import "./assets/scss/main.scss";
import "./assets/scss/tailwind.scss";
import "@moznsystems/mozn-ui/dist/mozn-ui.css";
import "./assets/scss/_arabic-cascade.scss";
import { UserData } from "./modules/main/main.types";

initChunkErrorHandler();

const RouterErrorFallback = () => {
	const error = useRouteError();
	const normalizedError =
		error instanceof Error ? error : new Error(String(error));
	return <ErrorFallback error={normalizedError} showSidebar={false} />;
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
			retry: false,
		},
	},
});

(async () => {
	if (CONFIG_ERRORS.length > 0) {
		ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
			<MisconfiguredPage errors={CONFIG_ERRORS} />,
		);
		return;
	}

	const auth = await initAuth();
	const user = auth.getUser();

	if (user) {
		const [fingerprintBrowserSDK, isCookieConsentEnabled] = await Promise.all([
			// Self-hosted ships without the fingerprint SDK, so there is nothing to ask.
			IS_SELF_HOSTED
				? { enabled: false }
				: getFliptFlag({ flagKey: "browser-fingerprint-sdk" }),
			fetchIsCookieConsentEnabled(),
		]);

		startConsentManagement({ isEnabled: isCookieConsentEnabled });

		if (fingerprintBrowserSDK.enabled) {
			collectBrowserSignals({
				userId: user.sub,
				hashTimezone: IS_PRODUCTION,
				excludeSignals: IS_PRODUCTION ? ["currentLocation"] : [],
			});
		}

		if (!IS_SELF_HOSTED) {
			setTrackedUser({
				// `id` is the opaque Keycloak subject — Clarity hashes it client-side, so
				// no PII leaves the browser; the friendly name is what makes a recording
				// findable in the dashboard.
				clarity: {
					id: user.sub as string,
					friendlyName: `${user.given_name} ${user.family_name}`,
					tags: {
						tenant_id: user.tenant_id,
						tenant_name: user.tenant_name,
						role: user.resource_access?.fraud?.roles,
						environment: APP_ENV,
						app_version: appVersion,
					},
				},
				userpilot: {
					id: user.sub as string,
					name: `${user.given_name} ${user.family_name}`,
					email: user.email,
					companyId: user.tenant_id,
					companyName: user.tenant_name,
					createdAt: user.created_timestamp,
				},
			});
		}

		initSentry({ email: user.email });

		const i18nEnabled = await fetchIsI18nEnabled();

		const router = createBrowserRouter(
			createRoutesFromElements(
				i18nEnabled ? (
					<Route
						path="/*"
						element={
							<LocaleGate>
								<Main userData={user as UserData} />
							</LocaleGate>
						}
						errorElement={<RouterErrorFallback />}
					/>
				) : (
					<Route
						path="/*"
						element={<Main userData={user as UserData} />}
						errorElement={<RouterErrorFallback />}
					/>
				),
			),
		);

		ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
			<ErrorBoundary>
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} />
					<CookieConsent />
				</QueryClientProvider>
			</ErrorBoundary>,
		);
	}
})();
